import React, { useCallback, useEffect, useState } from 'react';
import Menu from 'components/menu';
import { useStore } from 'hooks';
import { ApplicationSupplyService, ContractService } from 'services';
import Modal from 'components/modal';
import { Button, Input } from 'components/ui/form';
import Select from 'components/ui/form/select';
import styles from './archive-execution.module.scss';

const ArchiveExecution = () => {
  const { setLoading, setUser } = useStore();
  const [supplyArchivableCount, setSupplyArchivableCount] = useState(null);
  const [contractArchivableCount, setContractArchivableCount] = useState(null);
  const [confirmType, setConfirmType] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState('supply');
  const [contractCutoffDate, setContractCutoffDate] = useState('');
  const contractArchiveMaxDate = `${new Date().getFullYear() - 1}-12-31`;
  const isContractCutoffDateInvalid = Boolean(contractCutoffDate)
    && contractCutoffDate > contractArchiveMaxDate;

  const loadSupplyArchivableCount = useCallback(async () => {
    const supplyResponse = await new ApplicationSupplyService({ setUser }).getArchivableCount();
    setSupplyArchivableCount(Number(supplyResponse?.count || 0));
  }, [setUser]);

  const loadContractArchivableCount = useCallback(async () => {
    if (!contractCutoffDate || isContractCutoffDateInvalid) {
      setContractArchivableCount(null);
      return;
    }

    const contractResponse = await new ContractService({ setUser }).getArchivableCount({
      cutoffDate: contractCutoffDate,
    });
    setContractArchivableCount(Number(contractResponse?.count || 0));
  }, [setUser, contractCutoffDate, isContractCutoffDateInvalid]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadSupplyArchivableCount();
      setLoading(false);
    })();
  }, [setLoading, loadSupplyArchivableCount]);

  useEffect(() => {
    (async () => {
      if (archiveTarget !== 'contract') {
        return;
      }
      setLoading(true);
      await loadContractArchivableCount();
      setLoading(false);
    })();
  }, [archiveTarget, contractCutoffDate, loadContractArchivableCount, setLoading]);

  const onConfirmArchive = async () => {
    setLoading(true);
    if (confirmType === 'supply') {
      await new ApplicationSupplyService({ setUser }).executeArchive();
    } else if (confirmType === 'contract') {
      await new ContractService({ setUser }).executeArchive({
        cutoffDate: contractCutoffDate,
      });
    }
    await loadSupplyArchivableCount();
    if (archiveTarget === 'contract') {
      await loadContractArchivableCount();
    }
    setConfirmType(null);
    setLoading(false);
  };

  const isConfirmOpen = Boolean(confirmType);
  const selectedCount = archiveTarget === 'supply' ? supplyArchivableCount : contractArchivableCount;
  const canExecute = archiveTarget === 'supply'
    ? selectedCount > 0
    : (Boolean(contractCutoffDate) && !isContractCutoffDateInvalid && selectedCount > 0);

  return (
    <>
      <div className={styles.menu}>
        <Menu />
      </div>
      <div className={styles.inner}>
        <h2>Выполнить архивацию</h2>
        <div className={styles.selector}>
          <Select
            value={archiveTarget}
            onChange={({ target: { value } }) => setArchiveTarget(value)}
          >
            <option value="supply">Заявки на поставку</option>
            <option value="contract">Договоры</option>
          </Select>
        </div>
        {archiveTarget === 'contract' ? (
          <div className={styles.cutoff}>
            <Input
              type="date"
              label="Дата отсечки"
              value={contractCutoffDate}
              max={contractArchiveMaxDate}
              onChange={({ target: { value } }) => setContractCutoffDate(value)}
            />
          </div>
        ) : null}
        {archiveTarget === 'contract' && isContractCutoffDateInvalid ? (
          <p>Дата отсечки не может быть позже {contractArchiveMaxDate.split('-').reverse().join('.')}</p>
        ) : null}
        {!canExecute && archiveTarget === 'contract' && !contractCutoffDate ? (
          <p>Выберите дату отсечки для архивации договоров</p>
        ) : selectedCount > 0 ? (
          <>
            <p>
              {archiveTarget === 'supply'
                ? (
                  <>
                    К архивации доступно заявок на поставку:
                    {' '}
                    <b>{selectedCount}</b>
                  </>
                )
                : (
                  <>
                    К архивации доступно договоров:
                    {' '}
                    <b>{selectedCount}</b>
                  </>
                )}
            </p>
            <div className={styles.actions}>
              <Button
                className={styles.buttonAuto}
                disabled={!canExecute}
                onClick={() => setConfirmType(archiveTarget)}
              >
                Выполнить архивацию
              </Button>
            </div>
          </>
        ) : (
          <p>
            {archiveTarget === 'supply'
              ? 'Пока нечего архивировать (заявки на поставку)'
              : 'Пока нечего архивировать (договоры)'}
          </p>
        )}
      </div>
      {isConfirmOpen ? (
        <Modal variant="dialog" onClose={() => setConfirmType(null)}>
          <div className={styles.confirm}>
            <p>
              {confirmType === 'supply'
                ? 'Выполнить архивацию доступных заявок на поставку?'
                : (
                  <>
                    Выполнить архивацию доступных договоров
                    {contractCutoffDate ? ` до ${contractCutoffDate}` : ''}
                    ?
                  </>
                )}
            </p>
            <div className={styles.confirmActions}>
              <Button className={styles.buttonAuto} color="lightgray" onClick={() => setConfirmType(null)}>
                Отмена
              </Button>
              <Button className={styles.buttonAuto} onClick={onConfirmArchive}>
                Подтвердить
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
};

export default ArchiveExecution;
